import { FC, useEffect, useRef } from 'react';

import Dinero from 'dinero.js';
import { useFeatureFlag } from 'context/FeatureFlagContext';
import {
  filterVisibleKpiConfigs,
  GET_PORTFOLIO_SUMMARY_QUERY,
  resolveKpiValue,
} from 'features/pages/portfolio/PortfolioSummary';
import { useTourContext } from 'tours/TourProvider';

import { useQuery } from '@apollo/client';

import { AssetClass } from '__generated__/globalTypes';
import { GetPools } from 'query/__generated__/getPools';
import { GET_POOLS } from 'query/getPools';

import {
  GetPortfolioSummary,
  GetPortfolioSummaryVariables,
} from '../__generated__/PortfolioSummary';
import { PortfolioLayout } from '../Portfolio/PortfolioLayout';

import { DashboardKpiRow, DashboardKpiRowSkeleton } from './DashboardKpiRow';

const usePortfolioSummary = (assetClass?: AssetClass, skipCondition?: boolean) => {
  const { data, loading } = useQuery<GetPortfolioSummary, GetPortfolioSummaryVariables>(
    GET_PORTFOLIO_SUMMARY_QUERY,
    {
      variables: {
        hasAssetClass: !!assetClass,
        assetClass,
      },
      skip: skipCondition,
      fetchPolicy: 'cache-and-network',
      pollInterval: 15000,
    },
  );
  return { data, loading };
};

type KpiConfig = {
  description: string;
  selector: string;
  display: (value: number | null | undefined) => string;
};

type KpiWithValue = KpiConfig & {
  value: string;
};

const mapKpis = (data: GetPortfolioSummary | undefined): KpiWithValue[] => {
  const performanceData = data
    ? data.user.company.assetClassPerformanceData || data.user.company.performanceData
    : undefined;

  return filterVisibleKpiConfigs(performanceData).map((kpi) => {
    if (!data) {
      return {
        ...kpi,
        value: '-',
      };
    }

    const value = resolveKpiValue(performanceData, kpi.selector);
    return {
      ...kpi,
      value: kpi.description === 'UPB' ? formatUpb(value ?? 0) : kpi.display(value),
    };
  });
};

const formatUpb = (value: number) => {
  const truncatedValue = Math.floor(value);
  return Dinero({
    amount: truncatedValue,
    currency: 'USD',
  }).toFormat('$0,0');
};

type AssetClassSectionProps = {
  title: string;
  subtitle?: string;
  poolsCount: number;
  kpis: KpiWithValue[];
  poolsDataTour?: string;
  testIdPrefix?: string;
};

const AssetClassSection: FC<AssetClassSectionProps> = ({
  title,
  subtitle,
  poolsCount,
  kpis,
  poolsDataTour,
  testIdPrefix,
}) => (
  <div
    className="flex flex-col gap-5"
    data-testid={testIdPrefix ? `${testIdPrefix}-section` : undefined}
  >
    <div className="flex flex-col gap-1">
      <h1 className="text-3xl font-bold text-white">{title}</h1>
      {subtitle && <h2 className="text-2xl font-bold text-gray-400">{subtitle}</h2>}
      <div
        className="text-lg text-gray-400"
        data-tour={poolsDataTour}
        data-testid={testIdPrefix ? `${testIdPrefix}-pools-count` : undefined}
      >
        {poolsCount} pools
      </div>
    </div>
    <DashboardKpiRow kpis={kpis} data-testid-prefix={testIdPrefix} />
  </div>
);

const PortfolioSkeleton: FC = () => {
  return (
    <div className="flex w-full justify-center">
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-8 py-8">
        <div className="flex flex-col gap-5">
          <div className="flex flex-col gap-1">
            <div className="h-8 w-60 animate-pulse rounded bg-gray-700 opacity-50" />
            <div className="h-6 w-20 animate-pulse rounded bg-gray-600 opacity-50" />
          </div>
          <DashboardKpiRowSkeleton />
        </div>

        {[...Array(3)].map((_, idx) => (
          <div key={idx} className="flex flex-col gap-5">
            <div className="flex flex-col gap-1">
              <div className="h-9 w-32 animate-pulse rounded bg-gray-700 opacity-50" />
              <div className="h-7 w-48 animate-pulse rounded bg-gray-600 opacity-50" />
              <div className="h-6 w-20 animate-pulse rounded bg-gray-600 opacity-50" />
            </div>
            <DashboardKpiRowSkeleton />
          </div>
        ))}
      </div>
    </div>
  );
};

const PortfolioDashboard = () => {
  const isCreEnabled = useFeatureFlag('creEnabled');
  const isHelocEnabled = useFeatureFlag('helocEnabled');
  const isMarineEnabled = useFeatureFlag('marineEnabled');
  const { startTour, tourDataLoading } = useTourContext();
  const hasStartedTours = useRef(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startTourRef = useRef(startTour);
  const retryAttemptsRef = useRef(0);

  const { data, loading } = usePortfolioSummary();
  const { data: autoData, loading: autoLoading } = usePortfolioSummary(AssetClass.AUTO);
  const { data: homeData, loading: homeLoading } = usePortfolioSummary(AssetClass.HOME);
  const { data: unsecData, loading: unsecLoading } = usePortfolioSummary(AssetClass.UNSECPERSONAL);
  const { data: creData, loading: creLoading } = usePortfolioSummary(AssetClass.CRE, !isCreEnabled);
  const { data: helocData, loading: helocLoading } = usePortfolioSummary(
    AssetClass.HELOC,
    !isHelocEnabled,
  );
  const { data: marineData, loading: marineLoading } = usePortfolioSummary(
    AssetClass.MARINE,
    !isMarineEnabled,
  );
  const { data: poolsData } = useQuery<GetPools>(GET_POOLS, {
    fetchPolicy: 'cache-and-network',
    pollInterval: 15000,
  });

  const kpis = mapKpis(data);
  const autoKpis = mapKpis(autoData);
  const homeKpis = mapKpis(homeData);
  const creKpis = mapKpis(creData);
  const helocKpis = mapKpis(helocData);
  const marineKpis = mapKpis(marineData);

  const autoPools =
    poolsData?.user.company.pools.filter((listing) => listing.asset_class === AssetClass.AUTO)
      .length || 0;
  const homePools =
    poolsData?.user.company.pools.filter((listing) => listing.asset_class === AssetClass.HOME)
      .length || 0;
  const unsecPools =
    poolsData?.user.company.pools.filter(
      (listing) => listing.asset_class === AssetClass.UNSECPERSONAL,
    ).length || 0;
  const crePools =
    poolsData?.user.company.pools.filter((listing) => listing.asset_class === AssetClass.CRE)
      .length || 0;
  const helocPools =
    poolsData?.user.company.pools.filter((listing) => listing.asset_class === AssetClass.HELOC)
      .length || 0;
  const marinePools =
    poolsData?.user.company.pools.filter((listing) => listing.asset_class === AssetClass.MARINE)
      .length || 0;

  const totalPools =
    autoPools +
    homePools +
    unsecPools +
    (isCreEnabled ? crePools : 0) +
    (isHelocEnabled ? helocPools : 0) +
    (isMarineEnabled ? marinePools : 0);

  const isLoading =
    loading ||
    autoLoading ||
    homeLoading ||
    unsecLoading ||
    (isCreEnabled && creLoading) ||
    (isHelocEnabled && helocLoading) ||
    (isMarineEnabled && marineLoading);

  useEffect(() => {
    startTourRef.current = startTour;
  }, [startTour]);

  useEffect(() => {
    if (hasStartedTours.current) {
      return;
    }

    if (tourDataLoading || isLoading) {
      return;
    }

    const checkTourElementsReady = (): boolean => {
      const firstElement = document.querySelector('[data-tour="dashboard-total-kpis"]');
      return firstElement !== null;
    };

    const attemptStartTour = () => {
      if (hasStartedTours.current || tourDataLoading || isLoading) {
        return;
      }

      if (checkTourElementsReady()) {
        retryAttemptsRef.current = 0;
        timeoutRef.current = setTimeout(() => {
          if (hasStartedTours.current || tourDataLoading || isLoading) {
            return;
          }
          hasStartedTours.current = true;
          startTourRef.current('portfolio.dashboard');
          timeoutRef.current = null;
        }, 300);
      } else if (retryAttemptsRef.current < 20) {
        retryAttemptsRef.current += 1;
        timeoutRef.current = setTimeout(attemptStartTour, 150);
      } else {
        console.warn('[Tour] Dashboard tour elements not found after max retries');
      }
    };

    attemptStartTour();

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [tourDataLoading, isLoading]);

  return (
    <PortfolioLayout hideSummery={true}>
      {isLoading ? (
        <PortfolioSkeleton />
      ) : (
        <div className="flex w-full justify-center">
          <div className="mx-auto flex w-full max-w-6xl flex-col gap-8 py-8">
            <div
              className="flex flex-col gap-5"
              data-tour="dashboard-total-kpis"
              data-testid="portfolio-dashboard-total-section"
            >
              <div className="flex flex-col gap-1">
                <h2 className="text-2xl font-bold text-white">Total in your Portfolio</h2>
                <div
                  className="text-lg text-gray-400"
                  data-testid="portfolio-dashboard-total-pools-count"
                >
                  {totalPools} pools
                </div>
              </div>
              <DashboardKpiRow kpis={kpis} data-testid-prefix="portfolio-dashboard-total" />
            </div>

            <AssetClassSection
              title="Auto"
              subtitle="Total by asset class"
              poolsCount={autoPools}
              kpis={autoKpis}
              testIdPrefix="portfolio-dashboard-auto"
            />

            <AssetClassSection
              title="Resi"
              subtitle="Total by asset class"
              poolsCount={homePools}
              kpis={homeKpis}
              poolsDataTour="dashboard-resi-pools"
            />

            {isCreEnabled && (
              <AssetClassSection
                title="CRE"
                subtitle="Total by asset class"
                poolsCount={crePools}
                kpis={creKpis}
              />
            )}

            {isHelocEnabled && (
              <AssetClassSection
                title="HELOC"
                subtitle="Total by asset class"
                poolsCount={helocPools}
                kpis={helocKpis}
              />
            )}

            {isMarineEnabled && (
              <AssetClassSection
                title="Marine"
                subtitle="Total by asset class"
                poolsCount={marinePools}
                kpis={marineKpis}
              />
            )}

            <AssetClassSection
              title="Unsecured Personal"
              subtitle="Total by asset class"
              poolsCount={unsecPools}
              kpis={mapKpis(unsecData)}
            />
          </div>
        </div>
      )}
    </PortfolioLayout>
  );
};

export default PortfolioDashboard;
